import React, { useState, useEffect } from "react";
import {
  Card,
  Steps,
  Button,
  Row,
  Col,
  Space,
  Typography,
  Spin,
  notification,
} from "antd";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { usePermissions } from "../../../PermissionsProvider";
import { DatasetService } from "../../../services/DatasetService";
import DatasetSampling from "./Steps/DatasetSampling";
import DatasetName from "./Steps/DatasetName";
import DatasetReview from "./Steps/DatasetReview";
import DatasetBaseType from "./Steps/DatasetBaseType";
import { DatasetData } from "../../../model_gen/dataset";

const { Step } = Steps;
const { Text } = Typography;

export const NEW_DATASET_STORAGE_KEY = "datasetData";

const DatasetNew: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { apiKey } = usePermissions();
  const [loading, setLoading] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(() => {
    const step = searchParams.get("step");
    return step ? parseInt(step) : 0;
  });

  const [datasetData, setDatasetData] = useState<DatasetData>(() => {
    const saved = localStorage.getItem(NEW_DATASET_STORAGE_KEY);
    return saved
      ? JSON.parse(saved)
      : {
          sample_type: "all",
          sample_percentage: 100,
        };
  });

  // Save data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(NEW_DATASET_STORAGE_KEY, JSON.stringify(datasetData));
  }, [datasetData]);

  // Update URL when step changes
  useEffect(() => {
    setSearchParams((params) => {
      params.set("step", currentStep.toString());
      return params;
    });
  }, [currentStep, setSearchParams]);

  const steps = [
    {
      title: "Base Type",
      component: (
        <DatasetBaseType
          baseType={datasetData.base_type}
          intentGroupId={datasetData.intent_group_id}
          applicationId={datasetData.application_id}
          intentId={datasetData.intent_id}
          ignoredApplicationIds={datasetData.ignored_application_ids}
          onChange={(data: Partial<DatasetData>) =>
            setDatasetData((prev) => ({ ...prev, ...data }))
          }
        />
      ),
    },
    {
      title: "Sampling Method",
      component: (
        <DatasetSampling
          baseType={datasetData.base_type}
          intentGroupId={datasetData.intent_group_id}
          applicationId={datasetData.application_id}
          intentId={datasetData.intent_id}
          sampleType={datasetData.sample_type}
          samplePercentage={datasetData.sample_percentage}
          dateStart={datasetData.date_start}
          dateEnd={datasetData.date_end}
          onChange={(data: Partial<DatasetData>) =>
            setDatasetData((prev) => ({ ...prev, ...data }))
          }
          ignoredApplicationIds={datasetData.ignored_application_ids}
        />
      ),
    },
    {
      title: "Name & Description",
      component: (
        <DatasetName
          name={datasetData.name}
          description={datasetData.description}
          onChange={(name: string, description: string) =>
            setDatasetData((prev) => ({ ...prev, name, description }))
          }
        />
      ),
    },
    {
      title: "Review",
      component: <DatasetReview datasetData={datasetData} apiKey={apiKey} />,
    },
  ];

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        if (datasetData.base_type === "intent_group") {
          return datasetData.intent_group_id !== undefined;
        } else if (datasetData.base_type === "application_intent") {
          return (
            datasetData.application_id !== undefined &&
            datasetData.intent_id !== undefined
          );
        }
        return false;
      case 1:
        if (datasetData.sample_type === "random") {
          return (
            datasetData.sample_percentage !== undefined &&
            datasetData.sample_percentage > 0 &&
            datasetData.sample_percentage <= 100
          );
        }
        return true;
      case 2:
        return datasetData.name && datasetData.name.trim().length > 0;
      case 3:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const createDataset = async () => {
    setLoading(true);

    const payload: Partial<DatasetData> = {
      name: datasetData.name,
      description: datasetData.description || "",
      dataset_type: datasetData.dataset_type || "TRAIN",
      sample_type: datasetData.sample_type || "all",
      sample_percentage: datasetData.sample_percentage || 100,
      ignored_application_ids: datasetData.ignored_application_ids || [],
    };

    // Add date range if specified
    if (datasetData.date_start) {
      payload.date_start = datasetData.date_start;
    }

    if (datasetData.date_end) {
      payload.date_end = datasetData.date_end;
    }

    if (
      datasetData.base_type === "intent_group" &&
      datasetData.intent_group_id
    ) {
      payload.intent_group_id = datasetData.intent_group_id;
    } else if (
      datasetData.base_type === "application_intent" &&
      datasetData.application_id &&
      datasetData.intent_id
    ) {
      payload.application_id = datasetData.application_id;
      payload.intent_id = datasetData.intent_id;
    }

    DatasetService.createDataset(payload, setLoading, apiKey).subscribe(
      (response: any) => {
        notification.success({
          message: "Dataset created successfully",
          description: "Your dataset has been created successfully",
        });
        localStorage.removeItem(NEW_DATASET_STORAGE_KEY);
        navigate(`/models/datasets/${response.id}`);
      },
      (error: any) => {
        console.error("Error creating dataset:", error);
        notification.error({
          message: "Error creating dataset",
          description:
            error.message || "An error occurred while creating the dataset",
        });
      }
    );
  };

  return (
    <Card className="datasets-wrapper">
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Typography style={{ fontSize: "24px", fontWeight: "600" }}>
              <Link to="/models/datasets" style={{ color: "#828282" }}>
                Datasets
                {" / "}{" "}
              </Link>
              New
            </Typography>
            <Text
              type="secondary"
              style={{ display: "block", marginBottom: "24px" }}>
              Step {currentStep + 1} of {steps.length}
            </Text>
          </Col>
        </Row>

        <Steps current={currentStep} progressDot style={{ margin: "12px 0" }}>
          {steps.map((step) => (
            <Step key={step.title} title={step.title} />
          ))}
        </Steps>

        <Card
          bordered={false}
          className="step-content"
          style={{
            padding: "24px",
            borderRadius: "8px",
            margin: "12px 0",
          }}>
          {steps[currentStep].component}
        </Card>

        <Row justify="space-between">
          <Col>
            {currentStep > 0 && (
              <Button size="large" onClick={handlePrev} disabled={loading}>
                Previous Step
              </Button>
            )}
          </Col>
          <Col>
            {currentStep < steps.length - 1 ? (
              <Button
                type="primary"
                size="large"
                onClick={handleNext}
                disabled={!canProceed() || loading}>
                Continue
              </Button>
            ) : (
              <Button
                type="primary"
                size="large"
                disabled={!canProceed() || loading}
                onClick={createDataset}
                icon={loading && <Spin size="small" />}>
                {loading ? "Creating dataset..." : "Create Dataset"}
              </Button>
            )}
          </Col>
        </Row>
      </Space>
    </Card>
  );
};

export default DatasetNew;
